import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Handshake,
  Globe,
  Heart,
  ArrowRight,
  CheckCircle,
  Buildings,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const partnerTypes = [
  {
    icon: Buildings,
    title: "Development Partners",
    description: "International development organizations and NGOs working to advance sustainable development in Africa.",
    benefits: [
      "Joint program implementation",
      "Shared expertise and resources",
      "Expanded reach and impact",
      "Knowledge exchange",
    ],
  },
  {
    icon: Globe,
    title: "Government Agencies",
    description: "National and local government bodies committed to community development and poverty reduction.",
    benefits: [
      "Policy alignment",
      "Institutional support",
      "Scaling successful models",
      "Sustainable impact",
    ],
  },
  {
    icon: Heart,
    title: "Donors & Foundations",
    description: "Philanthropic organizations and individuals supporting grassroots development initiatives.",
    benefits: [
      "Transparent fund utilization",
      "Regular impact reporting",
      "Site visits and engagement",
      "Recognition and visibility",
    ],
  },
  {
    icon: Handshake,
    title: "Corporate Partners",
    description: "Businesses investing in community development as part of their CSR and shared value strategies.",
    benefits: [
      "Community impact investment",
      "Employee engagement programs",
      "Skills transfer opportunities",
      "Brand alignment with purpose",
    ],
  },
];

const currentPartners = [
  { name: "Partner Organization 1", category: "Development Partner" },
  { name: "Partner Organization 2", category: "Government Agency" },
  { name: "Partner Organization 3", category: "Foundation" },
  { name: "Partner Organization 4", category: "Corporate Partner" },
  { name: "Partner Organization 5", category: "Development Partner" },
  { name: "Partner Organization 6", category: "Foundation" },
];

const partnershipBenefits = [
  "Access to grassroots communities across Uganda",
  "Proven program models with demonstrated impact",
  "Experienced implementation team",
  "Strong monitoring and evaluation systems",
  "Transparent financial management",
  "Collaborative approach to partnership",
];

export default function PartnershipsPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-green-950" data-testid="partnerships-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Partnerships
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Partners & Donors
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed">
              We believe in the power of partnerships to drive sustainable change. Join us in building thriving communities across Africa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20 md:py-28" data-testid="why-partner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Why Partner With PEDA
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Together, We Create Lasting Impact
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                PEDA brings deep community connections, proven program models, and a commitment to sustainable development. Our partnerships are built on mutual respect, shared goals, and transparent collaboration.
              </p>

              <div className="space-y-4">
                {partnershipBenefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 shrink-0 mt-0.5" weight="fill" />
                    <p className="text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/cnkb2n0r_prefects-body-Serere-Campus-scaled.jpg" 
                alt="Partnership"
                className="w-full h-[450px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-500 rounded-2xl p-6 max-w-xs shadow-xl">
                <Handshake className="h-10 w-10 text-green-950 mb-3" weight="duotone" />
                <p className="text-green-950 font-semibold">
                  Building partnerships that transform communities and create sustainable prosperity.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="partner-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Ways to Partner
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Partnership Opportunities
            </h2>
            <p className="text-lg text-slate-600">
              We work with diverse partners to maximize our collective impact on communities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {partnerTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                  <type.icon className="h-7 w-7 text-green-700" weight="duotone" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{type.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">{type.description}</p>
                
                <div className="space-y-2">
                  {type.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-700 shrink-0" weight="fill" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className="py-20 md:py-28" data-testid="current-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Partners
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Organizations We Work With
            </h2>
            <p className="text-lg text-slate-600">
              We are grateful for the support of our partners who make our work possible.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {currentPartners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-50 rounded-2xl p-6 flex flex-col items-center justify-center text-center hover:bg-slate-100 transition-colors"
              >
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-3 shadow-sm">
                  <Buildings className="h-8 w-8 text-slate-400" weight="duotone" />
                </div>
                <p className="text-sm font-medium text-slate-700">{partner.name}</p>
                <p className="text-xs text-slate-500 mt-1">{partner.category}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8 text-sm">
            And many more community partners and supporters across Uganda.
          </p>
        </div>
      </section>

      {/* How to Partner */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="how-to-partner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
                Get Started
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                How to Partner With Us
              </h2>
              <p className="text-lg text-green-100/80 leading-relaxed mb-8">
                We welcome partnerships of all sizes. Whether you're looking to fund a specific project, collaborate on program implementation, or provide technical support, we'd love to explore how we can work together.
              </p>

              <div className="space-y-6">
                {[
                  { step: "1", title: "Reach Out", description: "Contact us to express your interest in partnership." },
                  { step: "2", title: "Explore Alignment", description: "We'll discuss your goals and identify areas of mutual interest." },
                  { step: "3", title: "Design Collaboration", description: "Together, we'll develop a partnership framework that works for both parties." },
                  { step: "4", title: "Create Impact", description: "We implement programs together, tracking progress and celebrating successes." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shrink-0">
                      <span className="font-bold text-green-950">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-green-100/70 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Partner?</h3>
              <p className="text-green-100/80 mb-6 leading-relaxed">
                We're excited to explore partnership opportunities with organizations and individuals committed to sustainable community development.
              </p>
              <Button 
                asChild 
                size="lg" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl"
                data-testid="partnerships-contact-button"
              >
                <Link to="/contact">
                  Contact Our Partnership Team <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
